import { useCallback, useState } from 'react'
import type { MapaCargo, MapaCargoSecao } from '@/components/Configuracao/mapaCargos/mapaCargosTypes'
import { useMapaCargosEscopoEdit } from '@/hooks/useMapaCargosOverrides'
import type { MapaCargoOverridesMap } from '@/components/Configuracao/mapaCargos/mapaCargosMerge'

export type MapaCargosModalTab = 'info' | 'escopo'

type Params = {
  cargos: MapaCargo[]
  overrides: MapaCargoOverridesMap
  resolveCargo: (id: string | null | undefined) => MapaCargo | undefined
  saveEscopo: (cargoId: string, secoes: MapaCargoSecao[]) => Promise<void>
  resetEscopo: (cargoId: string) => Promise<void>
  canEditEscopo: boolean
}

export function useMapaCargosTab(params: Params) {
  const { cargos, overrides, resolveCargo, saveEscopo, resetEscopo, canEditEscopo } = params
  const [selectedCargoId, setSelectedCargoId] = useState<string | null>(null)
  const [modalTab, setModalTab] = useState<MapaCargosModalTab>('info')

  const selectedCargo = selectedCargoId ? resolveCargo(selectedCargoId) ?? null : null
  const hasOverride = selectedCargoId ? Boolean(overrides[selectedCargoId]) : false

  const escopoEdit = useMapaCargosEscopoEdit({
    cargo: selectedCargo,
    hasOverride,
    onSave: async (secoes) => {
      if (!selectedCargoId) return
      await saveEscopo(selectedCargoId, secoes)
    },
    onReset: async () => {
      if (!selectedCargoId) return
      await resetEscopo(selectedCargoId)
    },
  })

  const cancelEscopoEdit = escopoEdit.cancelEdit

  const openCargo = useCallback((cargo: MapaCargo) => {
    setSelectedCargoId(cargo.id)
    setModalTab('info')
    cancelEscopoEdit()
  }, [cancelEscopoEdit])

  const closeModal = useCallback(() => {
    setSelectedCargoId(null)
    cancelEscopoEdit()
  }, [cancelEscopoEdit])

  const handleTabChange = useCallback(
    (tab: MapaCargosModalTab) => {
      if (tab !== 'escopo') cancelEscopoEdit()
      setModalTab(tab)
    },
    [cancelEscopoEdit],
  )

  return {
    cargos,
    selectedCargo,
    modalTab,
    setModalTab: handleTabChange,
    openCargo,
    closeModal,
    canEditEscopo,
    escopoEdit,
  }
}
